import React from 'react';

import { Spinner } from '../spinner/spinner';
import useAccountLogo, { AccountLogoStatus } from './account-logo.controller';

export const DEFAULT_LOGO = '/assets/images/telloport_logo_horizontal.png';

export interface AccountLogoProps extends Omit<React.ImgHTMLAttributes<HTMLImageElement>, 'src'> {
    accountId: string;
    fallback?: React.ReactNode;
    /** Image src to use when no account blob is found or the request fails. Defaults to the built-in Telloport logo. */
    fallbackSrc?: string;
    loadingFallback?: React.ReactNode;
    errorFallback?: React.ReactNode;
    onStatusChange?: (status: AccountLogoStatus) => void;
}

export const AccountLogo = (props: AccountLogoProps) => {
    const {
        accountId,
        fallback = null,
        fallbackSrc,
        loadingFallback = <Spinner />,
        errorFallback,
        onStatusChange,
        alt,
        ...imgProps
    } = props;
    const { src, status } = useAccountLogo(accountId);
    const defaultAlt = alt ?? 'Account logo';

    const renderImage = (imageSrc: string) => (
        <img
            {...imgProps}
            src={imageSrc}
            alt={defaultAlt}
        />
    );

    React.useEffect(() => {
        onStatusChange?.(status);
    }, [onStatusChange, status]);

    if (status === 'loading' && loadingFallback !== undefined) return <>{loadingFallback}</>;
    if (status === 'error' && errorFallback != null) return <>{errorFallback}</>;
    if (!src) {
        if (fallback !== undefined && fallback !== null) return <>{fallback}</>;
        return renderImage(fallbackSrc ?? DEFAULT_LOGO);
    }

    return renderImage(src);
};
